import { useState, useCallback } from "react";
import { useApiConfig, type ApiConfig } from "@visioni/a3s-client-api/app";
import { expandEndpoint } from "@visioni/a3s-client-api";
import { ArtifactUrlResponse, getReportArtifactUrlEndpoint } from "@visioni/a3s-reports/client-messages";

/**
 * On-demand `.xlsx` workbook download for one RoadSense site.
 *
 * The workbook is large and the customer only wants it on click, so unlike the
 * render payload (fetched eagerly by `useRoadSenseReport`) this presigns lazily:
 * the screen calls `download()` from the button handler, which presigns the
 * site's `workbookS3Key` via the generic artifact-url route, then navigates the
 * browser to the signed S3 URL to start the download.
 *
 * Presigning on click (rather than rendering an `<a href>` up front) keeps the
 * short-lived URL fresh — a link minted on page load would expire while the
 * report sits open.
 *
 * Web-only: it drives `window.location` to trigger the browser download. The
 * RoadSense screens are already web-gated, so there is no native branch.
 */
export interface UseRoadSenseWorkbookDownloadResult {
  readonly download: () => void;
  readonly isDownloading: boolean;
  readonly error: Error | null;
}

export function useRoadSenseWorkbookDownload(
  reportId: string | undefined,
  workbookS3Key: string | undefined,
): UseRoadSenseWorkbookDownloadResult {
  const apiConfig = useApiConfig();
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const download = useCallback(() => {
    if (reportId === undefined || workbookS3Key === undefined || isDownloading) return;
    setIsDownloading(true);
    setError(null);
    void (async () => {
      try {
        const url = await presignWorkbook(apiConfig, reportId, workbookS3Key);
        // Navigating the top-level location to a presigned URL whose response
        // carries `Content-Disposition: attachment` triggers a download without
        // leaving the SPA. (S3 sets the disposition from the object metadata the
        // generator stamped on the `.xlsx`.)
        window.location.href = url;
      } catch (e) {
        setError(e instanceof Error ? e : new Error("Workbook download failed"));
      } finally {
        setIsDownloading(false);
      }
    })();
  }, [apiConfig, reportId, workbookS3Key, isDownloading]);

  return { download, isDownloading, error };
}

async function presignWorkbook(apiConfig: ApiConfig, reportId: string, workbookS3Key: string): Promise<string> {
  const { tenant, authenticatedFetch } = apiConfig;
  if (!tenant) {
    throw new Error("Tenant not configured");
  }
  const path = expandEndpoint(getReportArtifactUrlEndpoint, {
    path: { tenantId: tenant.tenantId, type: "roadsense", reportId },
    query: { key: workbookS3Key },
  });
  const url = `${tenant.apiBaseUrl}${path}`;
  const response = await authenticatedFetch(url, {});
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Workbook presign failed: ${String(response.status)}`);
  }
  const body: unknown = await response.json();
  return ArtifactUrlResponse.parse(body).data.url;
}
